import React, { useState, useEffect } from 'react';
import { 
  Save,
  X
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import { mapCacheService, type MapCacheSettings as MapSettings } from '@/services/maps/MapCacheService';

interface MapCacheSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const MapCacheSettings: React.FC<MapCacheSettingsProps> = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState<MapSettings>(mapCacheService.getSettings());

  useEffect(() => {
    if (isOpen) {
      setSettings(mapCacheService.getSettings());
    }
  }, [isOpen]);

  const handleChange = (field: keyof MapSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = () => {
    mapCacheService.saveSettings(settings);
    alert('Configurações salvas com sucesso!');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configurações de Cache de Mapa">
      <div className="space-y-6">
        <Card>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raio de Cache (KM)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="10"
                  value={settings.cacheRadius}
                  onChange={(e) => handleChange('cacheRadius', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Área ao redor de cada ponto que será armazenada offline
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiração do Cache (Meses)
                </label>
                <select
                  value={settings.cacheExpiration}
                  onChange={(e) => handleChange('cacheExpiration', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={3}>3 meses</option>
                  <option value={6}>6 meses</option>
                  <option value={12}>12 meses</option>
                  <option value={24}>24 meses</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Máximo de Pontos
                </label>
                <select
                  value={settings.maxCachePoints}
                  onChange={(e) => handleChange('maxCachePoints', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={25}>25 pontos</option>
                  <option value={50}>50 pontos</option>
                  <option value={100}>100 pontos</option>
                  <option value={200}>200 pontos</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Raio de Busca de Endereços (metros)
                </label>
                <select
                  value={settings.addressCacheRadius * 1000}
                  onChange={(e) => handleChange('addressCacheRadius', parseFloat(e.target.value) / 1000)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={50}>50 metros</option>
                  <option value={100}>100 metros</option>
                  <option value={200}>200 metros</option>
                  <option value={500}>500 metros</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Distância máxima para encontrar endereços em cache
                </p>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.autoCache}
                    onChange={(e) => handleChange('autoCache', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Cache automático ao visitar novos locais
                  </span>
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* Espaço reservado para configurações adicionais, se necessário */}

        {/* Footer */}
        <div className="flex justify-end space-x-3">
          <Button variant="secondary" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button onClick={handleSaveSettings}>
            <Save className="w-4 h-4 mr-2" />
            Salvar Configurações
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MapCacheSettings;
